<template>
    <article>
        <h1 v-if="label">{{ label }}</h1>

        <ul :style="{ width: width }">
            <li
                v-for="(checkListItem, index) in checkList"
                :key="`check-list-item-${index}`"
                :title="getTitle(checkListItem)"
            >
                <x-text-field
                    v-model="checkListItem[itemValue]"
                    :class="{
                        'not-editable': !checkListItem.editable,
                        'is-checked': checkListItem.selected
                    }"
                    :rules="itemRules"
                    :append-icon="checkListItem.editable ? 'delete_outline' : null"
                    :hide-details="itemHideDetails"
                    :readonly="!checkListItem.editable"
                    @mouseover="scrollTextForwards"
                    @mouseout="scrollTextBackwards"
                    @click:append="removeItem(checkListItem, index)"
                >
                    <template v-slot:prepend-inner>
                        <x-checkbox v-model="checkListItem.selected" hide-details />
                    </template>
                </x-text-field>
            </li>
        </ul>

        <x-btn v-if="!hideAddition" color="tertiary" small block @click="addItem">
            <x-icon left>add</x-icon> {{ additionText }}
        </x-btn>

        <x-switch
            v-model="checkListValid"
            class="check-list-switch"
            :rules="[() => errorMessages.length === 0 || errorMessages[0]]"
        />
    </article>
</template>

<script>
import CheckIcon from '~/components/Shared/UIComponents/CheckIcon';
import Scroll from '~/mixins/scrollText';

export default {
    name: 'CheckList',
    components: {
        CheckIcon,
    },
    mixins: [Scroll],
    props: {
        value: Array,
        label: String,
        rules: {
            type: Array,
            default: () => []
        },
        width: String,
        itemValue: {
            type: String,
            default: 'value'
        },
        itemRules: Array,
        itemHideDetails: Boolean,
        hideAddition: Boolean,
        additionText: {
            type: String,
            default: 'Add item'
        },
        requireAllSelected: Boolean,
        showTitle: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            checkList: this.value,
            checkListValid: false
        }
    },
    computed: {
        errorMessages() {
            return this.rules.reduce((messages, rule) => {
                const ruleResult = rule(this.checkList);

                if (typeof ruleResult === 'string') {
                    messages.push(ruleResult);
                }

                return messages;
            }, []);
        }
    },
    watch: {
        checkList: {
            handler(newList) {
                this.$emit('input', newList);
            },
            deep: true
        },
        errorMessages(newValue) {
            this.checkListValid = newValue.length === 0;
        }
    },
    methods: {
        getTitle(checkListItem) {
            return this.showTitle ? checkListItem[this.itemValue] : null
        },
        addItem() {
            this.checkList.push({
                [this.itemValue]: '',
                editable: true,
                selected: true
            });
        },
        removeItem(item, index) {
            if (item.editable === false) {
                return;
            }

            this.checkList.splice(index, 1);
        }
    }
}
</script>

<style lang="scss" scoped>
h1 {
    margin-bottom: 8px;
    font-family: $font-body;
    font-size: rem(14px);
    font-weight: 600;
    line-height: 1em;
    letter-spacing: normal;
}

ul {
    display: flex;
    flex-direction: column;

    li {
        margin-top: -1px;

        .x-input {
            :deep() > .x-input__control > .x-input__slot {
                border: 1px solid rgba(255, 255, 255, 0.25);
                flex-direction: row;
                align-items: center;
                transition: $transition-length border;

                &:hover {
                    border: 1px solid rgba(255, 255, 255, .5);
                    z-index: 3;
                }

                .x-text-field__slot {
                    background: transparent !important;
                    border-color: transparent !important;

                    input {
                        padding-left: 0;
                    }
                }

                .x-input__prepend-inner {
                    margin: 0;
                    padding: 11px;

                    .x-input--checkbox {
                        margin: 0;
                        border: 0;
                        padding: 0;


                        .x-input__slot {
                            border: none;

                            .x-input--selection-controls__input {
                                margin: 0;
                            }
                        }

                    }
                }
            }

            &.x-input--is-focused {
                > :deep(.x-input__control) > .x-input__slot {
                    border-color: rgba(255, 255, 255, .5) !important;
                    z-index: 3;
                }
            }

            &.is-checked {
                &.x-input--is-focused {
                    > :deep(.x-input__control) > .x-input__slot {
                        border-color: $color-secondary !important;
                    }
                }

                > :deep(.x-input__control) > .x-input__slot {
                    border-color: $color-secondary;
                    z-index: 1;

                    .x-text-field__slot {
                        color: $color-secondary;
                    }
                }
            }

            &.not-editable,
            &:not(.x-input--is-focused) {
                > :deep(.x-input__control) > .x-input__slot {
                    background: transparent;
                }
            }

            &.ellipsis {
                text-overflow: ellipsis;
            }

            &.error--text {
                > :deep(.x-input__control) > .x-input__slot {
                    border-color: $color-error;
                    z-index: 2;
                }
            }
        }
    }
}

.x-btn {
    color: $color-grey-5;
    margin-top: 10px;

    i {
        margin-right: 5px;
    }
}

.check-list-switch {
    margin: 10px 0 0;
    padding: 0;

    :deep(.x-input__control) {
        flex-grow: 1;

        .x-input__slot {
            display: none;
        }
    }
}
</style>
